import type { FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import type { PrepareConnectorFlowStepResponse } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';

export type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export const hasMappingsUi = (config: FlowMappingsConfig | null | undefined): boolean =>
  !!config?.schema?.properties && Object.keys(config.schema.properties).length > 0;

type ConnectorFlowErrorData = {
  message?: string;
  content?: string;
};

const isConnectorFlowErrorData = (value: unknown): value is ConnectorFlowErrorData => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record['message'] === 'string' || typeof record['content'] === 'string';
};

/**
 * Pull the connector error body from common RTK Query rejection shapes:
 * - `{ data }` — FetchBaseQueryError / `.unwrap()` rejection
 * - `{ payload: { data } }` — full rejected action from DevTools / some catch sites
 * - the body itself when it already has `message` / `content`
 */
const extractConnectorFlowErrorData = (error: unknown): ConnectorFlowErrorData | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;

  if (isConnectorFlowErrorData(record['data'])) return record['data'];

  const payload = record['payload'];
  if (payload && typeof payload === 'object') {
    const payloadData = (payload as Record<string, unknown>)['data'];
    if (isConnectorFlowErrorData(payloadData)) return payloadData;
  }

  if (isConnectorFlowErrorData(error)) return error;

  return undefined;
};

/**
 * Prefer the specific `content`, then `message`, then undefined.
 * The connectors put the reason the user can act on in the details and the generic
 * "could not do it" in the message, so the details win wherever this is rendered — and
 * `message` still covers the errors the API raises on its own, which carry no details.
 * Values may already be translated (via transformErrorResponse) or still be i18n keys.
 */
export const resolveConnectorFlowErrorMessage = (error: unknown, lang: string): string | undefined => {
  const data = extractConnectorFlowErrorData(error);
  const rawKey = data?.content || data?.message;
  if (!rawKey) {
    return undefined;
  }
  return translateI18nValues(rawKey, lang);
};
